import _ from 'underscore';
import { Component, createRef } from 'react';
import styled from 'styled-components';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { FileExtension } from '~/file-extension';
import { theme } from '../../../styles';
import FileUploaderWithProgress from '../../common/file_uploader_with_progress';
import ClickOff from '../../common/click-off';
import AcademyIconSvgDefaultAvatar from './academy-icon-svg-default-avatar';
import CountingBadge from './academy-theme-sub-menu/counting-badge';

export default class AcademyUserAvatar extends Component {
  state = {
    subMenuVisible: false,
  };

  DEFAULT_WIDTH = 30;

  DEFAULT_SUBMENU_WIDTH = 150;

  WHITE_HEX = '#ffffff';

  firstFocusableElementRef = createRef();

  lastFocusableElementRef = createRef();

  componentDidUpdate() {
    const { current: firstMenuOption } = this.firstFocusableElementRef;
    const { current: lastMenuOption } = this.lastFocusableElementRef;

    if (this.state.subMenuVisible && firstMenuOption) {
      this.focusTrap = (e) => {
        const { activeElement } = document;

        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            if (activeElement === firstMenuOption) {
              lastMenuOption.focus();
              return;
            }
            activeElement.previousElementSibling.focus();
            break;
          case 'ArrowDown':
            e.preventDefault();
            if (activeElement === lastMenuOption) {
              firstMenuOption.focus();
              return;
            }
            activeElement.nextElementSibling.focus();
            break;
          case 'Escape':
            this.closeSubMenu();
            break;
          case 'Tab':
            e.preventDefault();
            break;
          default:
            break;
        }
      };

      firstMenuOption.focus();
      window.addEventListener('keydown', this.focusTrap);
    }

    if (this.state.subMenuVisible === false) {
      window.removeEventListener('keydown', this.focusTrap);
    }
  }

  getSubMenuOptionRef(currentOptionIndex, numOfOptions) {
    if (currentOptionIndex === 0) {
      return this.firstFocusableElementRef;
    }

    if (currentOptionIndex === numOfOptions - 1) {
      return this.lastFocusableElementRef;
    }

    return null;
  }

  render() {
    /*
      Cher => CH
      John Doe => JD
      Jane Doe Smith = JS
    */
    let initials;
    let placeholder = '';
    if (!this.props.imageSrc) {
      const names = this.props.name.split(' ');
      initials = names[0].substring(0, 1);
      initials += names.length > 1 ? names[names.length - 1].substring(0, 1) : names[0].substring(1, 2);

      placeholder = this.props.useInitials ? (
        initials
      ) : (
        <AcademyIconSvgDefaultAvatar
          color={this.props.textColor || this.WHITE_HEX}
          hoverColor={this.props.textHoverColor || this.WHITE_HEX}
          width={this.props.width || this.DEFAULT_WIDTH}
        />
      );
    }

    let subMenu = '';
    if (!_.isEmpty(this.props.subMenu) && this.state.subMenuVisible) {
      const avatarWidth = this.props.width || this.DEFAULT_WIDTH;
      subMenu = (
        <ClickOff onDOMClick={this.closeSubMenu}>
          <AcademyAvatarSubMenu
            className={academyCustomCSS.header.accountMenuPopup}
            avatarWidth={avatarWidth}
            role="menu"
            subMenuWidth={this.props.subMenuWidth || this.DEFAULT_SUBMENU_WIDTH}
            id="academy-avatar-menu"
            aria-labelledby="academy-avatar-menu-toggle"
          >
            {this.props.subMenu.map((menuItem, index) => (
              <AcademyAvatarSubMenuOption
                role="menuitem"
                href={menuItem.href}
                key={menuItem.linkText}
                onClick={(event) => {
                  event.preventDefault();
                  menuItem.clickHandler();
                }}
                className={academyCustomCSS.header.accountMenuPopupItem}
                ref={this.getSubMenuOptionRef(index, this.props.subMenu.length)}
              >
                {menuItem.linkText}
                {!!menuItem.badgeCount && (
                  <CountingBadge
                    badgeCount={menuItem.badgeCount}
                    subnavActiveLinkColor={this.props.subnavActiveLinkColor}
                  />
                )}
              </AcademyAvatarSubMenuOption>
            ))}
          </AcademyAvatarSubMenu>
        </ClickOff>
      );
    }

    const renderAvatar = (
      <AcademyAvatar
        $backgroundColor={this.props.backgroundColor || theme.color.darkBlue}
        className={`${this.props.className || ''} ${academyCustomCSS.header.accountMenuIcon}`}
        height={this.props.height || this.props.width || this.DEFAULT_WIDTH}
        $imageSrc={this.props.imageSrc}
        $lineHeight={this.props.lineHeight || this.props.height || this.props.width || this.DEFAULT_WIDTH}
        style={this.props.style || {}}
        $textColor={this.props.textColor || this.WHITE_HEX}
        width={this.props.width || this.DEFAULT_WIDTH}
      >
        {placeholder}
      </AcademyAvatar>
    );

    if (this.props.subMenu) {
      return (
        <AcademyAvatarWrapper height={this.props.height || this.props.width || this.DEFAULT_WIDTH}>
          <AcademyAvatarSubMenuToggle
            id="academy-avatar-menu-toggle"
            type="button"
            onClick={this.toggleSubMenu}
            aria-label="User Account Options"
            aria-controls="academy-avatar-menu"
            aria-expanded={this.state.subMenuVisible}
            aria-haspopup
          >
            {renderAvatar}
          </AcademyAvatarSubMenuToggle>
          {subMenu}
        </AcademyAvatarWrapper>
      );
    }

    return (
      <AcademyAvatarWrapper
        height={this.props.height || this.props.width || this.DEFAULT_WIDTH}
        id="academy-avatar-wrapper"
      >
        {renderAvatar}
        {this.props.editable && (
          <AcademyAvatarUploader
            className="academy-avatar-uploader"
            height={this.props.height || this.props.width || this.DEFAULT_WIDTH}
            onClick={this.props.imageSrc ? this.props.handleRemoveClick : () => {}}
            width={this.props.width || this.DEFAULT_WIDTH}
          >
            {this.props.imageSrc ? <img src="/images/cross_icon_blue.svg" /> : <img src="/images/attach_icon.svg" />}
            {!this.props.imageSrc && (
              <FileUploaderWithProgress
                prepareUploadUrl={this.props.prepareUploadUrl}
                uploadCompletedUrl={this.props.uploadCompletedUrl}
                uploadCompleted={this.props.uploadCompleted}
                filter={function (filename) {
                  return FileExtension(filename).isImage();
                }}
                failedFilterMessage="You seem to have selected a file that is not an image. Please select an image to upload."
              />
            )}
          </AcademyAvatarUploader>
        )}
      </AcademyAvatarWrapper>
    );
  }

  toggleSubMenu = () => {
    this.setState({
      subMenuVisible: !this.state.subMenuVisible,
    });
  };

  closeSubMenu = () => {
    this.setState({
      subMenuVisible: false,
    });
  };
}

const AcademyAvatarWrapper = styled.div`
  position: relative;
  height: ${(props) => props.height}px;
  &:hover {
    .academy-avatar-uploader {
      opacity: 1;
    }
  }
`;

/*
  > setting line-height to the same as the height ensures the text is vertically centered
  > math in font-size is to ensure the text always takes up a reasonable size relative to the height
*/
const AcademyAvatar = styled.div`
  display: inline-block;
  box-sizing: border-box;
  border-radius: 50%;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  line-height: ${(props) => props.$lineHeight}px;
  text-align: center;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor} !important;
  text-decoration: none;
  text-transform: uppercase;
  font-size: ${(props) => props.height / 2.25}px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  position: relative;
  ${(props) =>
    props.$imageSrc &&
    `
    background: url(${props.$imageSrc}) center center no-repeat;
    background-color: transparent;
    background-size: cover;
  `}
  &:hover {
    text-decoration: none;
  }
`;

const AcademyAvatarSubMenuToggle = styled.button`
  background: transparent;
  border: none;
  display: inline-block;
`;

const AcademyAvatarSubMenuOption = styled.a`
  white-space: nowrap;
  cursor: pointer;
`;

const AcademyAvatarSubMenu = styled.div`
  border-radius: 2px;
  background-color: #fff;
  box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.5);
  text-align: center;
  transition: all 0.3s ease-in-out;
  margin-top: 15px;
  text-transform: none;
  position: absolute;
  top: 100%;
  right: 0;
  min-width: ${(props) => props.subMenuWidth}px;

  & > ${AcademyAvatarSubMenuOption} {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 10px;
    color: #282828;
    font-size: 0.875rem;
    border-bottom: solid 1px #d8d8d8;
    transition: all 0.3s ease-in-out;
    background-color: #fff;
    line-height: 30px;

    &:first-child {
      border-radius: 2px 2px 0 0;
    }
    &:last-child {
      border: none;
      border-radius: 0 0 2px 2px;
    }
    &:hover {
      text-decoration: none;
      background-color: #d8d8d8;
    }
  }

  &:after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #fff;
    position: absolute;
    right: ${(props) => props.avatarWidth / 2 - 5}px;
    top: -5px;
  }
`;

const AcademyAvatarUploader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  overflow: hidden;
  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
  }
  .file-uploader-link,
  .file-uploader-link a {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
`;
